// eslint-disable-next-line no-restricted-imports
import {AvatarSizes, Facepile as DIGFacepile} from '@dropbox/dig-components/avatar';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {loggedInEmployeeAtom} from 'atoms/employee';
import type {Employee} from 'client';
import {Link} from 'components/DSYS/Link';
import {OptionalTooltip} from 'components/DSYS/OptionalTooltip';
import {ROUTE_PATHS} from 'constant';
import {sortEmployees, sortEmployeesSelfFirst} from 'helpers/utils';
import {useEmployeesBatchSuspense} from 'hooks/useEmployee';
import {useAtomValue} from 'jotai';
import {ReactNode, Suspense} from 'react';
import {getBackendUrl, getProfileImageUrl, nameToInitials} from 'utilities';

/**
 * Facepile is for a stack of employee avatars. For a single avatar, see DSYS/Avatar.tsx
 *
 * @param ldaps - list of ldap strings
 * @param size - size of the avatars
 * @param overflow - number of avatars to show before truncating
 * @param ownerIsFirst - whether the owner of the facepile should be first
 * @param showSelf - whether to show the logged in user in the facepile
 * @param hideTooltip - whether to hide the tooltip
 * @param onClick - function to call when the facepile is clicked
 * @param linkToProfile - whether each face links to that person's profile page
 * @param onProfileLinkClick - called when a profile link face is clicked
 */
export const Facepile = ({
  ldaps,
  size,
  overflow = 3,
  ownerIsFirst,
  showSelf = true,
  shouldSort = true,
  hideTooltip,
  onClick,
  forceDisabled,
  disabledLdaps,
  linkToProfile,
  onProfileLinkClick,
}: {
  ldaps: string[];
  hideTooltip?: boolean;
  size?: AvatarSizes;
  overflow?: number;
  ownerIsFirst?: boolean;
  showSelf?: boolean;
  shouldSort?: boolean;
  forceDisabled?: boolean;
  disabledLdaps?: string[];
  onClick?: () => void;
  linkToProfile?: boolean;
  onProfileLinkClick?: (ldap: string) => void;
}) => (
  <DIGFacepile size={size}>
    <Suspense fallback={null}>
      <LoadableEmployeeFacepile
        size={size}
        hideTooltip={Boolean(hideTooltip)}
        ownerIsFirst={Boolean(ownerIsFirst)}
        showSelf={Boolean(showSelf)}
        shouldSort={Boolean(shouldSort)}
        ldaps={ldaps}
        overflow={overflow}
        forceDisabled={Boolean(forceDisabled)}
        disabledLdaps={disabledLdaps}
        onClick={onClick}
        linkToProfile={Boolean(linkToProfile)}
        onProfileLinkClick={onProfileLinkClick}
      />
    </Suspense>
  </DIGFacepile>
);

export const orderEmployeesByLdaps = (employees: Employee[], ldaps: string[]): Employee[] => {
  const employeesByLdap = new Map(employees.map((employee) => [employee.ldap, employee]));
  return Array.from(new Set(ldaps))
    .map((ldap) => employeesByLdap.get(ldap))
    .filter((employee): employee is Employee => Boolean(employee));
};

export const sortFacepileEmployees = ({
  employees,
  currentEmployee,
  shouldSort,
  ownerIsFirst,
  showSelf,
}: {
  employees: Employee[];
  currentEmployee: Employee;
  shouldSort: boolean;
  ownerIsFirst: boolean;
  showSelf: boolean;
}): Employee[] => {
  if (!shouldSort) {
    return employees;
  }

  const comparator = showSelf ? sortEmployeesSelfFirst(currentEmployee) : sortEmployees;

  if (ownerIsFirst && employees.length > 0) {
    const [owner, ...rest] = employees;
    return [owner, ...[...rest].sort(comparator)];
  }

  return [...employees].sort(comparator);
};

// Wrap a face in a profile link without disturbing the facepile's overlap
// layout (DIG styles the avatar items themselves, so the anchor is transparent).
const ProfileLinkWrapper = ({
  ldap,
  name,
  children,
  onClick,
}: {
  ldap: string;
  name: string;
  children: ReactNode;
  onClick?: (ldap: string) => void;
}) => (
  <Link
    to={ROUTE_PATHS.PROFILE.replace(':userid', ldap)}
    aria-label={name}
    hasNoUnderline
    monochromatic
    style={{display: 'flex'}}
    onClick={() => onClick?.(ldap)}
  >
    {children}
  </Link>
);

const LoadableEmployeeFacepile = ({
  ldaps,
  size,
  overflow,
  ownerIsFirst,
  showSelf,
  shouldSort,
  hideTooltip,
  forceDisabled,
  disabledLdaps,
  onClick,
  linkToProfile,
  onProfileLinkClick,
}: {
  ldaps: string[];
  size?: 'large' | 'medium' | 'small' | 'xsmall' | 'standard';
  overflow: number;
  hideTooltip: boolean;
  ownerIsFirst: boolean;
  forceDisabled: boolean;
  disabledLdaps?: string[];
  showSelf: boolean;
  shouldSort: boolean;
  onClick?: () => void;
  linkToProfile?: boolean;
  onProfileLinkClick?: (ldap: string) => void;
}) => {
  const {employee} = useAtomValue(loggedInEmployeeAtom);
  const employees = useEmployeesBatchSuspense({ldaps, enabled: ldaps.length > 0});
  const employeesInRequestedOrder = orderEmployeesByLdaps(employees, ldaps);
  const sortedEmployees = sortFacepileEmployees({
    employees: employeesInRequestedOrder,
    currentEmployee: employee,
    shouldSort,
    ownerIsFirst,
    showSelf,
  });

  const visibleEmployeeCount = sortedEmployees.length;
  const splitIndex = visibleEmployeeCount > overflow ? overflow - 1 : overflow;

  return (
    <>
      {visibleEmployeeCount > overflow && (
        <OptionalTooltip
          enabled={!hideTooltip}
          title={sortedEmployees
            .slice(splitIndex)
            .map((e) => e.name)
            .join(', ')}
          openDelay={300}
        >
          <DIGFacepile.Item
            colorPalette={{background: 'var(--dig-color__opacity__surface)', foreground: ''}}
          >
            <Box style={{cursor: onClick ? 'pointer' : 'default'}} onClick={onClick}>
              <Text
                isBold
                size={size === 'standard' ? 'small' : size === 'small' ? 'xsmall' : size}
              >
                +{visibleEmployeeCount - splitIndex}
              </Text>
            </Box>
          </DIGFacepile.Item>
        </OptionalTooltip>
      )}
      {sortedEmployees
        .slice(0, splitIndex)
        .reverse()
        .filter(Boolean)
        .map((contributor) => {
          const item = (
            <DIGFacepile.Item
              style={{
                cursor: onClick || linkToProfile ? 'pointer' : 'default',
                filter:
                  forceDisabled || contributor.is_deactivated
                    ? 'grayscale(1) opacity(.5)'
                    : undefined,
              }}
              onClick={onClick}
              name={contributor.name}
              isInactive={
                forceDisabled ||
                (contributor.is_deactivated ?? false) ||
                disabledLdaps?.includes(contributor.ldap)
              }
              src={
                !contributor.profile_photo_url
                  ? getBackendUrl() + getProfileImageUrl(contributor.ldap)
                  : contributor.profile_photo_url.startsWith('http')
                    ? contributor.profile_photo_url
                    : getBackendUrl() + contributor.profile_photo_url
              }
            >
              {nameToInitials(contributor.name)}
            </DIGFacepile.Item>
          );

          return (
            <OptionalTooltip
              enabled={!hideTooltip}
              key={contributor.user_id}
              title={
                contributor.is_deactivated ? `${contributor.name} (Deactivated)` : contributor.name
              }
              openDelay={300}
            >
              {linkToProfile && contributor.ldap ? (
                <ProfileLinkWrapper
                  ldap={contributor.ldap}
                  name={contributor.name}
                  onClick={onProfileLinkClick}
                >
                  {item}
                </ProfileLinkWrapper>
              ) : (
                item
              )}
            </OptionalTooltip>
          );
        })}
    </>
  );
};
